import React, { useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { message, Button, Form, Input, Modal, Divider, Row, Col } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Select, InputNumber, TextArea, DatePicker } from 'components/Form';
import EditGradeModal from 'components/Grading/EditGradeModal';
import { useT } from 'hooks/Locale/useT';
import useQueryApiClient from 'utils/useApiClient';
import '../styles.scss';
import moment from 'moment';
import { config } from '../../../config/config';

const formItemLayout = {
    labelCol: { span: 4 },
    wrapperCol: { span: 12 },
};

interface AssessmentFinalResultInterface {
    loadAssignment?: Function;
    activityFormData: any;
    assessmentSettings: any;
    submission: any;
    setIsSubmitLoading: React.Dispatch<React.SetStateAction<boolean>>;
    isSubmitLoading: boolean;
    onGraded: Function;
}

const AssessmentFinalResult: React.FC<AssessmentFinalResultInterface> = ({
    activityFormData,
    assessmentSettings,
    submission,
    setIsSubmitLoading,
    isSubmitLoading,
    onGraded,
    loadAssignment,
}) => {
    const intl = useIntl();

    // The modal must visibly refresh after grading actions - a no-op onGraded
    // (e.g. from the program results table) otherwise leaves stale data on
    // screen and the action looks like it did nothing.
    const refreshAfterAction = (): void => {
        loadAssignment?.();
        onGraded();
    };
    const t = useT();
    const [form] = Form.useForm();
    const [annulComment, setAnnulComment] = useState<string>('');

    // initialValues apply only on first mount - re-sync the fields whenever the
    // attempt data is refetched (after grade edit / annul / submit), otherwise
    // the tab keeps showing stale values until the modal is reopened.
    useEffect(() => {
        if (!activityFormData?.attemptData?.finalAssessment) {
            return;
        }

        form.setFieldsValue({
            grade: submission?.grade,
            grade_comments: activityFormData.attemptData.finalAssessment.comments,
            grade_date: activityFormData.attemptData.finalAssessment.gradedDate
                ? moment(activityFormData.attemptData.finalAssessment.gradedDate).startOf('day')
                : moment().startOf('day'),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityFormData]);
    const [isAnnulLoading, setIsAnnulLoading] = useState<boolean>(false);
    const [editGradeOpen, setEditGradeOpen] = useState<boolean>(false);

    const { appendData: submitFinalAssessment } = useQueryApiClient({
        request: {
            url: `/api/v1/courses/assessment-results/submit-final-assessment/${submission.id}`,
            method: 'POST',
        },
        onSuccess: (): void => {
            message.success(intl.formatMessage({ id: 'courses.evaluation_submitted_success' }));
            refreshAfterAction();
        },
        onFinally: (): void => setIsSubmitLoading(false),
    });

    const { appendData: submitAnnulAssessment } = useQueryApiClient({
        request: {
            url: `/api/v1/courses/learning-program/${activityFormData.programId}/enrollment/${activityFormData.enrollmentId}/annul-assessment`,
            method: 'POST',
        },
        onSuccess: (): void => {
            message.success(intl.formatMessage({ id: 'assessment.annulled_successfully' }));
            refreshAfterAction();
        },
        onFinally: (): void => setIsAnnulLoading(false),
    });

    const submitForm = async (values: any) => {
        setIsSubmitLoading(true);
        const parsedValues = {
            ...values,
            grade_date: values.grade_date ? moment(values.grade_date).format(config.DBDateFormat) : null,
        };
        submitFinalAssessment(parsedValues);
    };

    const { confirm } = Modal;

    const handleAnnul = (): void => {
        confirm({
            title: intl.formatMessage({ id: 'assessment.annul_confirmation_title' }),
            icon: <ExclamationCircleOutlined />,
            okText: intl.formatMessage({ id: 'general.confirm' }),
            cancelText: intl.formatMessage({ id: 'general.cancel' }),
            onOk() {
                setIsAnnulLoading(true);
                submitAnnulAssessment({
                    activityAttemptId: submission.attempt_id,
                    submissionId: submission.id,
                    comment: annulComment,
                });
            },
        });
    };

    // The grade input must follow the evaluation type frozen into the submission at initiation,
    // because that is what the backend validates the grade against. Live material settings may
    // have been switched afterwards, which would render an input the backend rejects.
    const evaluationType: string | undefined =
        activityFormData?.attemptData?.finalAssessment?.evaluationType ??
        submission?.submission_data?.evaluation_type ??
        assessmentSettings?.evaluation_type;

    const renderGradeValue = () => {
        switch (evaluationType) {
            case 'EXAMINATION':
                return (
                    <Select
                        name="grade"
                        label={intl.formatMessage({ id: 'grading.grade' })}
                        validation={{ required: true }}
                        customLayout={formItemLayout}
                        defaultValue={submission.grade ?? 0}
                        manualOptions={{
                            PASSED: intl.formatMessage({ id: 'general.passed' }),
                            FAILED: intl.formatMessage({ id: 'general.failed' }),
                        }}
                        disabled={!activityFormData.attemptData.finalAssessment.canSubmit}
                    />
                );

            case 'RANGE':
                return (
                    <InputNumber
                        name="grade"
                        label={intl.formatMessage({ id: 'grading.grade' })}
                        customRules={[
                            {
                                required: true,
                                message: intl.formatMessage({ id: 'validation.field_required' }),
                            },
                        ]}
                        customLayout={formItemLayout}
                        min={0}
                        max={100}
                        formatter={(value: number) => `${value}%`}
                        parser={(value: string) => value!.replace('%', '')}
                        disabled={!activityFormData.attemptData.finalAssessment.canSubmit}
                    />
                );
        }
    };

    return (
        <div className="assessment-final-result">
            {activityFormData.attemptData.finalAssessment.gradedAt && (
                <div className="flex gap-10 mb-10">
                    <span>{t('grading.grade_added_date')}:</span>
                    <span className="bold">
                        {moment(activityFormData.attemptData.finalAssessment.gradedAt).format('DD.MM.YYYY HH:mm')}
                    </span>
                </div>
            )}
            <Form
                className="assessment-final-result-form"
                form={form}
                onFinish={submitForm}
                initialValues={{
                    grade: submission.grade,
                    grade_comments: activityFormData.attemptData.finalAssessment.comments,
                    grade_date: activityFormData?.attemptData?.finalAssessment?.gradedDate
                        ? moment(activityFormData.attemptData.finalAssessment.gradedDate).startOf('day')
                        : moment().startOf('day'),
                }}
            >
                {renderGradeValue()}
                <DatePicker
                    name={'grade_date'}
                    label={intl.formatMessage({ id: 'grading.grade_date' })}
                    customLayout={formItemLayout}
                    format={config.defaultDateFormat}
                    disabled={!activityFormData.attemptData.finalAssessment.canSubmit}
                />
                <TextArea
                    name="grade_comments"
                    label={intl.formatMessage({ id: 'grading.grade_comment' })}
                    customLayout={formItemLayout}
                    autoSize={{ minRows: 3 }}
                    showCount
                    disabled={!activityFormData.attemptData.finalAssessment.canSubmit}
                />
                {activityFormData.attemptData.finalAssessment.canSubmit && (
                    <div className="flex gap-10">
                        <Button type="primary" htmlType="submit" loading={isSubmitLoading}>
                            <FormattedMessage id="assessment.submit_assessment" />
                        </Button>
                    </div>
                )}

                {activityFormData.attemptData.finalAssessment.isSubmitted && activityFormData.canEditGrade && (
                    <div className="flex gap-10" style={{ marginBottom: '10px' }}>
                        <Button onClick={() => setEditGradeOpen(true)}>
                            <FormattedMessage id="grading.edit_grade" />
                        </Button>
                        <EditGradeModal
                            open={editGradeOpen}
                            onCancel={() => setEditGradeOpen(false)}
                            attemptId={submission.attempt_id}
                            evaluationType={assessmentSettings.evaluation_type}
                            initialGrade={submission.grade}
                            initialGradeDate={activityFormData?.attemptData?.finalAssessment?.gradedDate}
                            initialGradeComments={activityFormData.attemptData.finalAssessment.comments}
                            onSaved={() => {
                                setEditGradeOpen(false);
                                refreshAfterAction();
                            }}
                        />
                    </div>
                )}

                {submission?.status === 'PASSED' && activityFormData.attemptData.finalAssessment.canAnnul && (
                    <>
                        <Divider/>
                        <div className="flex gap-10 flex-column">
                            <TextArea
                                name="annul_comment"
                                label={intl.formatMessage({ id: 'grading.annul_comment' })}
                                placeholder={intl.formatMessage({ id: 'assessment.annul_comment_placeholder' })}
                                customLayout={formItemLayout}
                                onChange={(e: any) => setAnnulComment(e.target.value)}
                                autoSize={{ minRows: 3 }}
                                maxLength={200}
                                showCount
                            />
                            <Row>
                                <Col offset={formItemLayout.labelCol.span}>
                                    <Button danger loading={isAnnulLoading} onClick={handleAnnul}>
                                        <FormattedMessage id="assessment.annul_assessment" />
                                    </Button>
                                </Col>
                            </Row>
           
                        </div>
                    </>
                )}
            </Form>
        </div>
    );
};

export default AssessmentFinalResult;
